import type * as React from "react";
import { Repeat, Tag, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { cn } from "../../../lib/cn";
import { CitationHighlighter } from "../CitationHighlighter";
import type { CitationAnchor } from "../types";

export interface BankingTransactionPayload {
  /** Amount in cents to avoid float drift; sign reflects direction. */
  amountCents: number;
  /** ISO currency code, e.g. `EUR`. */
  currency: string;
  /** ISO timestamp of the booking date. */
  bookedAt: string;
  /** Counterparty label ("ACME S.p.A.", "Stipendio Maggio"…). */
  counterparty: string;
  /** Bank category (free text). */
  category?: string;
  /** Recurring detection hit? */
  isRecurring?: boolean;
  /** Optional memo / longer description. */
  memo?: string;
}

export interface TransactionCardProps {
  payload: BankingTransactionPayload;
  anchor?: CitationAnchor;
}

function formatEUR(cents: number, currency: string): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * TransactionCard — single banking transaction renderer for the Source Drawer.
 *
 * The whole card highlights when `anchor` is present (the transaction
 * itself is the citation atom). Inflow/outflow visual cue uses arrow icons
 * + brand orange for outflow (the "you spent" colour in the brand system).
 */
export const TransactionCard: React.FC<TransactionCardProps> = ({
  payload,
  anchor,
}) => {
  const cited = Boolean(anchor);
  const outflow = payload.amountCents < 0;

  const card = (
    <article className="flex flex-col gap-2 text-sm">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-semibold text-base text-ink-1 tracking-tight">
            {payload.counterparty}
          </div>
          <time
            className="font-mono text-[11px] text-ink-3 tabular-nums"
            dateTime={payload.bookedAt}
          >
            {new Date(payload.bookedAt).toLocaleDateString("it-IT", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </time>
        </div>
        <div
          className={cn(
            "flex items-baseline gap-1 font-display font-semibold text-lg tabular-nums",
            outflow ? "text-ando-orange" : "text-emerald-600",
          )}
        >
          {outflow ? (
            <ArrowDownRight className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          )}
          {formatEUR(payload.amountCents, payload.currency)}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-1.5">
        {payload.category ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border border-hairline",
              "bg-surface-1/70 px-2 py-0.5 text-[11px] text-ink-2",
            )}
          >
            <Tag className="h-3 w-3" aria-hidden />
            {payload.category}
          </span>
        ) : null}
        {payload.isRecurring ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full",
              "bg-ando-orange-soft px-2 py-0.5 font-medium text-[11px] text-ando-orange",
            )}
          >
            <Repeat className="h-3 w-3" aria-hidden />
            Ricorrente
          </span>
        ) : null}
      </div>

      {payload.memo ? (
        <p className="text-ink-2 text-xs">{payload.memo}</p>
      ) : null}
    </article>
  );

  return <CitationHighlighter active={cited}>{card}</CitationHighlighter>;
};
TransactionCard.displayName = "TransactionCard";
